import React, { useState, useRef } from 'react';
import { AppBar as MuiAppBar, Toolbar, Typography, Button, Box, Chip, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildApiUrl, API_CONFIG } from '../config';
import { apiFetch } from '../utils/auth';
import { clearTechnicalAccountFlag, clearRealUserIdentity } from '../utils/realUser';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import UpdateIcon from '@mui/icons-material/Update';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import OfflineIndicator from './OfflineIndicator';
import { useTutorial } from '../contexts/TutorialContext';
import { useTour } from '../contexts/TourContext';

// Hilfsfunktion um Sekunden in Stunden, Minuten und Sekunden zu formatieren
const formatTimeRemaining = (seconds: number): string => {
  if (seconds <= 0) return '0 Sekunden';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  const parts: string[] = [];
  
  if (hours > 0) {
    parts.push(`${hours} Stunde${hours !== 1 ? 'n' : ''}`);
  }
  
  if (minutes > 0) {
    parts.push(`${minutes} Minute${minutes !== 1 ? 'n' : ''}`);
  }
  
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds} Sekunde${remainingSeconds !== 1 ? 'n' : ''}`);
  }
  
  return parts.join(', ');
};

// Type definitions
interface TokenInfo {
  expiresAt: string;
  timeLeft: number;
  isExpired: boolean;
  payload: any;
}

interface DebugInfo {
  currentVersion: string;
  serverVersion: string;
  cacheKeys: string[];
  localStorageKeys: string[];
  sessionStorageKeys: string[];
  hasUpdate: boolean;
  tokenInfo: TokenInfo | null;
}

interface TopAppBarProps {
  einsatzleiterMode?: boolean;
}

const TopAppBar: React.FC<TopAppBarProps> = ({ einsatzleiterMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { forceResetTutorial, startTutorial } = useTutorial();
  const { forceStartTour } = useTour();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [showDebugCodeModal, setShowDebugCodeModal] = useState(false);
  const [debugCode, setDebugCode] = useState('');
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const clickCount = useRef(0);
  const clickTimer = useRef<NodeJS.Timeout | null>(null);
  const [buildInfo, setBuildInfo] = useState<any>(null);

  // Lazy load service worker hook to avoid circular dependencies
  React.useEffect(() => {
    const loadServiceWorker = async () => {
      try {
        const { getServiceWorkerManager } = await import('../utils/serviceWorkerManager');
        const swManager = getServiceWorkerManager();
        
        // Set up update callback directly on the manager
        swManager.setUpdateCallback((available: boolean) => {
          setUpdateAvailable(available);
        });
        
        // Check current update status
        setUpdateAvailable(swManager.isUpdateAvailable());
      } catch (error) {
        console.error('Failed to load service worker:', error);
      }
    };

    // Defer loading to avoid circular dependency issues
    setTimeout(loadServiceWorker, 200);
  }, []);

  // Username aus dem AccessToken extrahieren
  let username = '';
  const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  if (accessToken) {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      username = payload.username || payload.name || payload.email || '';
    } catch {}
  }

  // Load build info
  React.useEffect(() => {
    fetch('/build-info.json')
      .then(response => response.json())
      .then(data => {
        setBuildInfo(data);
      })
      .catch(() => setBuildInfo({ version: 'unknown', buildTime: 'unknown' }));
  }, []);

  // Timer für live countdown der Token-Sekunden
  React.useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (showDebugModal && debugInfo?.tokenInfo) {
      // Timer starten: jede Sekunde aktualisieren
      interval = setInterval(() => {
        setDebugInfo(prevDebugInfo => {
          if (!prevDebugInfo?.tokenInfo) return prevDebugInfo;
          
          const now = new Date();
          const timeLeftSeconds = Math.floor((prevDebugInfo.tokenInfo.payload.exp * 1000 - now.getTime()) / 1000);
          
          return {
            ...prevDebugInfo,
            tokenInfo: {
              ...prevDebugInfo.tokenInfo,
              timeLeft: timeLeftSeconds,
              isExpired: timeLeftSeconds <= 0
            }
          };
        });
      }, 1000);
    }
    
    // Cleanup: Timer stoppen wenn Modal geschlossen wird
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showDebugModal, debugInfo?.tokenInfo]);

  const handleLogout = async () => {
    const accessToken = localStorage.getItem('accessToken');
    // usrID und orgID aus dem AccessToken extrahieren (JWT decode)
    let usrID = 0, orgID = 0;
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        usrID = payload.id;
        orgID = payload.orgID;
      } catch {}
    }

    try {
      // Logout-Endpoint aufrufen (mit Credentials um HttpOnly-Cookie zu löschen)
      await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOGOUT), {
        method: 'POST',
        credentials: 'include', // Wichtig: Cookies mitsenden
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ usrID, orgID })
      });
      
      console.log('✅ Logout-Endpoint erfolgreich aufgerufen, Refresh-Cookie wird gelöscht');
    } catch (e) {
      console.warn('⚠️ Logout-Endpoint fehlgeschlagen, aber lokaler Logout wird durchgeführt:', e);
    }
    
    // Token-Manager stoppen und Storage leeren
    const cleanupTokenManager = async () => {
      try {
        const { getTokenManager } = await import('../utils/tokenManager');
        getTokenManager().cleanup();
      } catch (error) {
        console.error('Failed to cleanup token manager:', error);
      }
    };
    
    cleanupTokenManager();
    
    // Zusätzlich: Alle Auth-bezogenen Daten aus Storage entfernen
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loggedIn');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('loggedIn');
    
    // Technischer Account und Benutzeridentität zurücksetzen
    clearTechnicalAccountFlag();
    clearRealUserIdentity();
    
    console.log('🧹 Lokaler Storage geleert, Benutzer wird ausgeloggt');
    navigate('/');
  };

  const handleUpdate = () => {
    setShowUpdateModal(false);
    window.location.reload();
  };

  // Click counter handler für Debug-Modal
  const handleDebugClick = () => {
    clickCount.current += 1;
    
    if (clickCount.current === 1) {
      // Ersten Klick starten
      clickTimer.current = setTimeout(() => {
        clickCount.current = 0;
      }, 3000);
    } else if (clickCount.current === 5) {
      // Nach 5 Klicks Debug-Code-Modal öffnen
      setShowDebugCodeModal(true);
      clickCount.current = 0;
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
      }
    }
  };

  // Debug-Code überprüfen
  const handleDebugCodeSubmit = () => {
    if (debugCode === '4411') {
      setShowDebugCodeModal(false);
      setDebugCode('');
      
      // Debug-Informationen sammeln
      const currentVersion = localStorage.getItem('currentAppVersion') || 'unknown';
      const serverVersion = buildInfo?.version || 'unknown';
      
      // Token-Informationen sammeln
      let tokenInfo: TokenInfo | null = null;
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const expDate = new Date(payload.exp * 1000);
          const now = new Date();
          const timeLeftSeconds = Math.floor((payload.exp * 1000 - now.getTime()) / 1000); // Sekunden
          
          tokenInfo = {
            expiresAt: expDate.toLocaleString('de-DE'),
            timeLeft: timeLeftSeconds,
            isExpired: timeLeftSeconds <= 0,
            payload: payload
          };
        } catch (error) {
          console.error('Error decoding token:', error);
        }
      }
      
      // Cache-Informationen sammeln
      caches.keys().then(cacheKeys => {
        const debugData: DebugInfo = {
          currentVersion,
          serverVersion,
          cacheKeys,
          localStorageKeys: Object.keys(localStorage),
          sessionStorageKeys: Object.keys(sessionStorage),
          hasUpdate: currentVersion !== serverVersion,
          tokenInfo
        };
        setDebugInfo(debugData);
        setShowDebugModal(true);
      });
    } else {
      alert('Falscher Code!');
      setDebugCode('');
    }
  };

  // Cache löschen Funktionen
  const clearLocalStorage = () => {
    localStorage.clear();
    setShowDebugModal(false);
  };

  const clearCache = async () => {
    try {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map(key => caches.delete(key)));
      setShowDebugModal(false);
    } catch (error) {
      console.error('Fehler beim Löschen des Caches:', error);
    }
  };

  const clearApiCache = async () => {
    try {
      const cacheKeys = await caches.keys();
      // Only clear API cache, keep static cache
      const apiCacheKeys = cacheKeys.filter(key => key.includes('asm-api-v'));
      await Promise.all(apiCacheKeys.map(key => caches.delete(key)));
      alert('API Cache wurde gelöscht! Teams und Audit-Logs werden jetzt frisch geladen.');
      setShowDebugModal(false);
    } catch (error) {
      console.error('Fehler beim Löschen des API Caches:', error);
    }
  };

  // Button nur anzeigen, wenn nicht auf der Login-Seite UND User ist eingeloggt
  const showLogout = location.pathname !== '/' && (localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken'));

  // Modal Button Styles (wie in anderen Modals)
  const modalButtonStyle = {
    background: '#eee',
    color: '#222',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    margin: '0 8px',
    minWidth: 100
  };

  const modalButtonPrimaryStyle = {
    background: '#c00',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    margin: '0 8px',
    minWidth: 100
  };

  const modalButtonDangerStyle = {
    background: '#db2828',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    margin: '0 8px',
    minWidth: 100
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <MuiAppBar position="fixed" sx={{ background: '#c00', zIndex: 1201 }}>
        <Toolbar>
          {/* Logo ganz links */}
          <img src="/logo_landscape.png" alt="ASMonitor Logo" style={{ height: 30, marginRight: 12, marginLeft: 0, cursor: 'pointer' }} onClick={() => navigate('/dashboard')} />
          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />
          {/* Einsatzleiter-Modus Anzeige - Zentriert */}
          {einsatzleiterMode && (
            <Box sx={{ 
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', 
              alignItems: 'center', 
              background: '#1976d2', 
              borderRadius: 16, 
              padding: '6px 16px', 
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
            }}>
              <SupervisorAccountIcon sx={{ fontSize: 18, mr: 1, color: '#fff' }} />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                EINSATZLEITER-MODUS
              </Typography>
            </Box>
          )}
          {/* Update Button */}
          {showLogout && updateAvailable && (
            <Tooltip title="Neue Version verfügbar">
              <Chip
                size="small"
                icon={<UpdateIcon />}
                label="Update"
                onClick={() => setShowUpdateModal(true)}
                sx={{ 
                  height: 20, 
                  marginRight: 1,
                  cursor: 'pointer',
                  backgroundColor: 'rgb(255, 140, 0)',
                  color: '#fff',
                  '& .MuiChip-icon': {
                    color: '#fff !important'
                  },
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                    '100%': { opacity: 1 }
                  }
                }}
              />
            </Tooltip>
          )}
          {/* Offline Indicator */}
          {showLogout && (
            <Box sx={{ marginRight: '25px' }}>
              <OfflineIndicator />
            </Box>
          )}
          {/* Username und Logout nur wenn eingeloggt */}
          {showLogout && (
            <>
              {username && (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <AccountCircleIcon 
                    onClick={handleDebugClick}
                    onTouchStart={handleDebugClick}
                    sx={{ 
                      fontSize: 32, 
                      color: '#fff',
                      marginLeft: 2
                    }} 
                  />
                  <span style={{ fontWeight: 500, fontSize: 16 }}>{username}</span>
                </Box>
              )}
              <Button color="inherit" onClick={handleLogout} sx={{ minWidth: 0, padding: 1 }} title="Logout">
                <LogoutIcon />
              </Button>
            </>
          )}
        </Toolbar>
      </MuiAppBar>

      {/* Debug Code Modal */}
      {showDebugCodeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: 16, 
            padding: 24, 
            minWidth: 350, 
            maxWidth: 350, 
            position: 'relative', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            boxShadow: '0 4px 32px #0002' 
          }}>
            {/* Titelbereich oben */}
            <div id="modal-title" style={{ width: '100%', textAlign: 'center', fontWeight: 700, fontSize: 22, marginBottom: 20, letterSpacing: 1 }}>
              Debug-Code eingeben
            </div>
            
            {/* Mittlerer Bereich */}
            <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ fontSize: 16, color: '#333', marginBottom: 16 }}>
                Geben Sie den 4-stelligen Debug-Code ein:
              </div>
              <input
                type="password"
                value={debugCode}
                onChange={(e) => setDebugCode(e.target.value)}
                placeholder="****"
                maxLength={4}
                style={{
                  fontSize: 24,
                  textAlign: 'center',
                  padding: 12,
                  border: '2px solid #ddd',
                  borderRadius: 8,
                  width: 120,
                  letterSpacing: 8
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleDebugCodeSubmit();
                  }
                }}
                autoFocus
              />
            </div>
            
            {/* Button-Bereich unten */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, gap: 12 }}>
              <button onClick={() => { setShowDebugCodeModal(false); setDebugCode(''); }} style={modalButtonStyle}>
                Abbrechen
              </button>
              <button onClick={handleDebugCodeSubmit} style={modalButtonPrimaryStyle}>
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Confirmation Modal */}
      {showUpdateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: 16, 
            padding: 24, 
            minWidth: 450, 
            maxWidth: 450, 
            position: 'relative', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            boxShadow: '0 4px 32px #0002' 
          }}>
            {/* Titelbereich oben */}
            <div id="modal-title" style={{ width: '100%', textAlign: 'center', fontWeight: 700, fontSize: 22, marginBottom: 20, letterSpacing: 1 }}>
              Neue Version verfügbar
            </div>
            
            {/* Mittlerer Bereich */}
            <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ fontSize: 16, color: '#333', marginBottom: 16 }}>
                Eine neue Version der Anwendung ist verfügbar. 
                Möchten Sie die Anwendung jetzt aktualisieren?
              </div>
              <div style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}>
                Die Anwendung wird neu geladen und alle unsynchronisierten Daten werden gespeichert.
              </div>
            </div>
            
            {/* Button-Bereich unten */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
              <button onClick={() => setShowUpdateModal(false)} style={modalButtonStyle}>
                Abbrechen
              </button>
              <button onClick={handleUpdate} style={modalButtonPrimaryStyle}>
                Aktualisieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Modal */}
      {showDebugModal && debugInfo && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: 16, 
            padding: 24, 
            minWidth: 500, 
            maxWidth: 600, 
            maxHeight: '80vh',
            position: 'relative', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            boxShadow: '0 4px 32px #0002',
            overflow: 'auto'
          }}>
            {/* Titelbereich oben */}
            <div id="modal-title" style={{ width: '100%', textAlign: 'center', fontWeight: 700, fontSize: 22, marginBottom: 20, letterSpacing: 1 }}>
              Debug Information
            </div>
            
            {/* Mittlerer Bereich */}
            <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
              <div style={{ fontSize: 16, color: '#333', marginBottom: 16, width: '100%' }}>
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#856404', fontWeight: 'bold', marginBottom: 4 }}>
                    ⚠️ Wichtiger Hinweis:
                  </div>
                  <div style={{ fontSize: 12, color: '#856404' }}>
                    Die Funktionen "Speicher leeren" und "Cache leeren" sollten nur in Absprache mit dem Support ausgeführt werden, da sie alle lokalen Daten löschen.
                  </div>
                </div>
                
                <div style={{ marginBottom: 12 }}>
                  <strong>Versionen:</strong>
                  <div style={{ marginLeft: 16, fontSize: 14 }}>
                    <div>Lokale Version: <span style={{ color: debugInfo.hasUpdate ? '#db2828' : '#21ba45' }}>{debugInfo.currentVersion}</span></div>
                    <div>Server Version: <span style={{ color: debugInfo.hasUpdate ? '#db2828' : '#21ba45' }}>{debugInfo.serverVersion}</span></div>
                    {debugInfo.hasUpdate && <div style={{ color: '#db2828', fontWeight: 'bold' }}>⚠️ Update verfügbar!</div>}
                  </div>
                </div>
                
                {debugInfo.tokenInfo && (
                  <div style={{ marginBottom: 12 }}>
                    <strong>Access Token:</strong>
                    <div style={{ marginLeft: 16, fontSize: 14 }}>
                      <div>Läuft ab: <span style={{ color: debugInfo.tokenInfo.isExpired ? '#db2828' : debugInfo.tokenInfo.timeLeft < 30 ? '#fbbd08' : '#21ba45' }}>{debugInfo.tokenInfo.expiresAt}</span></div>
                      <div>Verbleibend: <span style={{ color: debugInfo.tokenInfo.isExpired ? '#db2828' : debugInfo.tokenInfo.timeLeft < 30 ? '#fbbd08' : '#21ba45' }}>
                        {debugInfo.tokenInfo.isExpired ? '0 Sekunden' : formatTimeRemaining(debugInfo.tokenInfo.timeLeft)}
                      </span></div>
                      {debugInfo.tokenInfo.isExpired && <div style={{ color: '#db2828', fontWeight: 'bold' }}>⚠️ Token abgelaufen!</div>}
                      {!debugInfo.tokenInfo.isExpired && debugInfo.tokenInfo.timeLeft < 30 && <div style={{ color: '#fbbd08', fontWeight: 'bold' }}>⚠️ Token läuft bald ab!</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Button-Bereich unten */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, flexWrap: 'wrap', gap: 8 }}>
              <button onClick={() => { forceResetTutorial(); startTutorial(); }} style={{ ...modalButtonStyle, background: '#c00', color: '#fff' }}>
                📚 Tutorial wiederholen
              </button>
              <button onClick={() => { forceStartTour(); setShowDebugModal(false); }} style={{ ...modalButtonStyle, background: '#007bff', color: '#fff' }}>
                🎯 Tour Testen
              </button>
              <button onClick={clearLocalStorage} style={modalButtonDangerStyle}>
                Speicher leeren
              </button>
              <button onClick={clearCache} style={modalButtonDangerStyle}>
                Cache leeren
              </button>
              <button onClick={clearApiCache} style={modalButtonDangerStyle}>
                API Cache leeren
              </button>
              <button onClick={() => setShowDebugModal(false)} style={modalButtonStyle}>
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default TopAppBar; 